import { EarlyBirdRuleWithUiId, EarlyBirdConditionWithUiId } from "app/pages/EarlyBirdOfferBuilder/actionTypes";
import { ProductSelectMode } from "app/shared/model/EarlyBirdOfferDto";

function stripUiId<T>(c: T & { uiId?: string }): T {
    const { uiId, ...rest } = c;
    return rest as T;
}

export function rulesToDto(rules: EarlyBirdRuleWithUiId[]): { rules: { name: string; earlyBirdEndDate: string; include: any[]; exclude: any[] }[] } {
    return {
        rules: rules.map((r) => ({
            name: r.name,
            earlyBirdEndDate: r.earlyBirdEndDate,
            include: r.include.map((c) => stripUiId({ ...c })),
            exclude: r.exclude.map((c) => stripUiId({ ...c })),
        })),
    };
}

function validateCondition(condition: EarlyBirdConditionWithUiId): string[] {
    const err: string[] = [];
    const field = condition.field;

    if (field === "admissionRegion") {
        const r = (condition as any).region;
        if (r == null || !String(r).trim()) {
            err.push("Admission Region is required.");
        }
    }

    if (field === "agent") {
        const agents = (condition as any).agents;
        if (!Array.isArray(agents) || agents.length === 0) {
            err.push("Agent: select at least one agent.");
        }
    }

    if (field === "age") {
        const age = condition as any;
        if (age.minAge == null && age.maxAge == null) {
            err.push("Age: set at least one of min or max.");
        }
        if (age.minAge != null && age.maxAge != null && age.minAge > age.maxAge) {
            err.push("Age: min must be <= max.");
        }
    }

    if (field === "arrivalDepartureDates") {
        const a = (condition as any).arrival;
        const d = (condition as any).departure;
        if (a?.from && a?.to && a.from > a.to) err.push("Arrival: from must be <= to.");
        if (d?.from && d?.to && d.from > d.to) err.push("Departure: from must be <= to.");
        if (!a?.from && !a?.to && !d?.from && !d?.to) err.push("Arrival/Departure: set at least one date.");
    }

    if (field === "bookingSystem") {
        if (!(condition as any).bookingSystemId) err.push("Booking System is required.");
    }

    if (field === "course") {
        const co = condition as any;
        if (co.mode == null) err.push("Course: mode is required.");
        if (co.minWeeks != null && co.maxWeeks != null && co.minWeeks > co.maxWeeks) {
            err.push("Course: weeks min must be <= max.");
        }
    }

    if (field === "accommodation") {
        const ac = condition as any;
        if (ac.mode === ProductSelectMode.Type && ac.kind == null) {
            err.push("Accommodation: Type mode requires kind.");
        }
        if (ac.minWeeks != null && ac.maxWeeks != null && ac.minWeeks > ac.maxWeeks) {
            err.push("Accommodation: weeks min must be <= max.");
        }
    }

    if (field === "addOn") {
        const ad = condition as any;
        if (ad.mode === ProductSelectMode.Type && ad.kind == null) {
            err.push("AddOn: Type mode requires kind.");
        }
    }

    if (field === "transfer") {
        const tr = condition as any;
        if (tr.mode == null) err.push("Transfer: mode is required.");
    }

    if (field === "saleCountry") {
        if (!(condition as any).countryCodes?.length) err.push("Sale Country: select at least one.");
    }

    if (field === "school") {
        if (!(condition as any).schoolIds?.length) err.push("School: select at least one.");
    }

    if (field === "studyStartEndDates") {
        const s = (condition as any).studyStartEnd;
        if (!s?.from && !s?.to) err.push("Study Start/End: set at least one date.");
        if (s?.from && s?.to && s.from > s.to) err.push("Study Start/End: from must be <= to.");
    }

    return err;
}

export function validateRule(rule: EarlyBirdRuleWithUiId): string[] {
    const err: string[] = [];
    if (!rule.name?.trim()) err.push("Rule name is required.");
    if (!rule.earlyBirdEndDate) err.push("Early Bird End Date is required.");
    for (const c of rule.include) err.push(...validateCondition(c).map((e) => `Include: ${e}`));
    for (const c of rule.exclude) err.push(...validateCondition(c).map((e) => `Exclude: ${e}`));
    return err;
}

export function validateRules(rules: EarlyBirdRuleWithUiId[]): string[] {
    const all: string[] = [];
    rules.forEach((r, i) => {
        const ruleErrs = validateRule(r);
        ruleErrs.forEach((e) => all.push(`Rule ${i + 1}: ${e}`));
    });
    return all;
}

export function hasValidationErrors(rules: EarlyBirdRuleWithUiId[]): boolean {
    return validateRules(rules).length > 0;
}